/**
 * 上に出る知らせの層 (2026-09-21「上に出る知らせ」G 案)。
 *
 * 画面の上 (タブの下端) に出してよいのは、タイムラインそのものに関わる 3 つだけ:
 *   1. 引っ張って更新の輪 (pull_refresh.ts) — 中身が下がって空いた所に輪。文字は出さない (X と同じ)
 *   2. その結果 — 「3 件の新しいポスト」「新しいポストはありません」「更新できませんでした [再試行]」
 *      「更新しました」を、高さ 44px の静かなカプセルで
 *   3. 新着ポストのピル (new-posts-pill.ts) — 押せる新着だけは紫で塗る
 * それ以外の操作の結果は下のトースト (toast.ts)。上と下で役割を分けるので、上に 2 枚以上は重ならない。
 * 遷移中の細い線 (nav-polish.ts が付ける html.is-navigating) もこの層に置く。
 *
 * 置き場所は「タブの下端」。ヘッダーとタブはスクロールで上へ抜ける (--chrome-p) ので、
 * 何かが出ている間だけ scroll に付いて測り直す (出ていなければ 1 回も測らない)。
 *
 * ⚠ body 直下に置く (.col-main / .mobile-header の backdrop-filter の中に fixed を置くと
 *   包含ブロックが変わって画面の奥へ飛ぶ)。CSS は v2.css §256。
 */

let layer: HTMLElement | null = null;
let following = 0; // 付いて測る必要のある表示の数

export function topLayer(): HTMLElement {
  if (layer && layer.isConnected) return layer;
  let el = document.getElementById("mpTopNotice");
  if (!el) {
    el = document.createElement("div");
    el.id = "mpTopNotice";
    el.className = "tn-layer";
    el.setAttribute("data-ptr", "idle");
    el.innerHTML =
      '<div class="tn-prog" aria-hidden="true"><i></i></div>' +
      '<div class="tn-ptr" aria-hidden="true">' +
        '<svg class="tn-ring" viewBox="0 0 32 32" focusable="false">' +
          '<circle class="tn-ring-track" cx="16" cy="16" r="12"/>' +
          '<circle class="tn-ring-arc" cx="16" cy="16" r="12" pathLength="100"/>' +
        "</svg>" +
      "</div>" +
      '<div class="tn-slot" role="status" aria-live="polite"></div>';
    document.body.appendChild(el);
  }
  layer = el;
  measure();
  return el;
}

export function topSlot(): HTMLElement {
  return topLayer().querySelector<HTMLElement>(".tn-slot") as HTMLElement;
}

/** タブ (無ければヘッダー) の下端と、メインの列の中央を測って CSS 変数へ。 */
export function measure(): void {
  const el = layer;
  if (!el) return;
  const tabs = document.querySelector<HTMLElement>(".col-main-header");
  const hdr = document.querySelector<HTMLElement>(".mobile-header");
  let top = 0;
  for (const cand of [tabs, hdr]) {
    if (!cand) continue;
    const r = cand.getBoundingClientRect();
    if (r.height > 0 && r.bottom > 0) { top = Math.max(top, r.bottom); }
  }
  el.style.setProperty("--tn-top", Math.round(top) + "px");
  // パルスクリーン (2026-09-22、partials/pulse_screen.html) が画面に見えている間は、新着ポストと
  // 結果のカプセルを **帯の下** に置く — タブ直下だと帯の上に被さる (オーナー指摘「更新のときに
  // 表示が被る」)。帯が上へ抜けたら従来どおりタブの下。引っ張って更新の輪は空いた所 (タブの下)
  // に出るので動かさない (--tn-band を読むのは .tn-slot だけ、v2.css §256)。
  const ps = document.querySelector<HTMLElement>("#mnBand.ps-screen");
  let band = 0;
  if (ps) {
    const r = ps.getBoundingClientRect();
    if (r.height > 0 && r.bottom > top && r.bottom < window.innerHeight / 2) band = Math.round(r.bottom - top);
  }
  el.style.setProperty("--tn-band", band + "px");
  const col = document.querySelector<HTMLElement>(".col-main");
  if (col && window.innerWidth >= 768) {
    const r = col.getBoundingClientRect();
    el.style.setProperty("--tn-cx", Math.round(r.left + r.width / 2) + "px");
  } else {
    el.style.removeProperty("--tn-cx");
  }
}

const onScroll = () => measure();
/** 付いて測るのを始める / やめる (出している数を数える) */
export function follow(on: boolean): void {
  following = Math.max(0, following + (on ? 1 : -1));
  if (on && following === 1) {
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    measure();
  } else if (!on && following === 0) {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onScroll);
  }
}

/* ---------- 結果のカプセル ---------- */

export type ResultKind = "new" | "none" | "fail" | "reloaded";

function icon(kind: "check" | "x" | "up"): string {
  const d = kind === "check" ? '<path d="M5 12.5l4.2 4.2L19 7"/>'
    : kind === "x" ? '<path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/>'
    : '<path d="M12 19.5V5"/><path d="M6 11l6-6 6 6"/>';
  return '<svg class="tn-i" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' + d + "</svg>";
}
export const TN_ICON = { up: icon("up"), check: icon("check"), x: icon("x") };

let resultTimer: number | null = null;

/** 結果を出す。新着ピルが出ていれば、結果が消えるまでしまっておく。 */
export function showResult(kind: ResultKind, count: number, retry?: () => void): void {
  const slot = topSlot();
  slot.querySelectorAll<HTMLElement>(".tn-res").forEach((n) => release(n));
  if (resultTimer) { clearTimeout(resultTimer); resultTimer = null; }
  const el = document.createElement("div");
  el.className = "tn-note tn-res tn-res--" + kind;
  let text = "";
  if (kind === "new") text = count + " 件の新しいポスト";
  else if (kind === "none") text = "新しいポストはありません";
  else if (kind === "reloaded") text = "更新しました";
  else text = "更新できませんでした";
  el.innerHTML = '<span class="tn-note-in">' + (kind === "fail" ? icon("x") : icon("check")) +
    '<span class="tn-txt"></span></span>';
  (el.querySelector(".tn-txt") as HTMLElement).textContent = text;
  if (kind === "fail" && retry) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "tn-act";
    b.textContent = "再試行";
    b.addEventListener("click", () => { hide(el); retry(); });
    (el.querySelector(".tn-note-in") as HTMLElement).appendChild(b);
  }
  slot.insertBefore(el, slot.firstChild);
  layer?.classList.add("has-res");
  el.dataset.f = "1";
  follow(true);
  // 引っ張って更新の直後は中身がまだ戻る途中 (pull_refresh.ts の setShift .36s) なので、
  // 帯の下端が落ち着いてから測り直す (パルスクリーンの下に置くとき)
  window.setTimeout(() => measure(), 420);
  requestAnimationFrame(() => el.classList.add("is-in"));
  window.setTimeout(() => el.classList.add("is-in"), 40);
  resultTimer = window.setTimeout(() => hide(el), kind === "fail" ? 5000 : 2000);
}

function hide(el: HTMLElement): void {
  if (el.classList.contains("is-out")) return;
  el.classList.remove("is-in");
  el.classList.add("is-out");
  window.setTimeout(() => release(el), 320);
}

/** 片付け (付いて測るのをやめる数え方は 1 枚につき 1 回だけ) */
function release(el: HTMLElement): void {
  if (el.dataset.f === "1") { el.dataset.f = ""; follow(false); }
  el.remove();
  if (!topSlot().querySelector(".tn-res")) layer?.classList.remove("has-res");
}

export function initTopNotice(): void {
  // 遷移中の線を出すために、層は最初から置いておく (中身が空なら何も見えない)
  topLayer();
  window.addEventListener("resize", () => measure());
  document.addEventListener("mp:navigated", () => measure());
  // 遷移中の線 (html.is-navigating は nav-polish.ts が付ける) は、付いた瞬間のタブの下端に出す
  // (スクロールでヘッダーが抜けていれば画面の上端)。
  try {
    new MutationObserver(() => {
      if (document.documentElement.classList.contains("is-navigating")) measure();
    }).observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  } catch (_) { /* ignore */ }
}
